import { ChangeDetectorRef, Component, NgZone, OnInit, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import {
  IonIcon,
  IonSpinner
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  alertCircleOutline,
  eyeOutline,
  eyeOffOutline,
  mailOutline,
  lockClosedOutline,
  personOutline,
  checkmarkCircle,
  closeCircle,
  createOutline
} from 'ionicons/icons';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AuthService, PasswordRequirements } from '../../../services/auth.service';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    IonIcon,
    IonSpinner,
    TranslatePipe
],
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent implements OnInit {
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  name = '';
  email = '';
  password = '';
  confirmPassword = '';
  showPassword = false;
  showConfirmPassword = false;
  isLoading = false;
  isProviderLoading = true;
  googleAvailable = false;
  errorMessage = '';
  acceptTerms = false;

  passwordRequirements: PasswordRequirements = {
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false
  };

  constructor(
    private authService: AuthService,
    private router: Router
  ) {
    addIcons({
      alertCircleOutline,
      eyeOutline,
      eyeOffOutline,
      mailOutline,
      lockClosedOutline,
      personOutline,
      checkmarkCircle,
      closeCircle,
      createOutline
    });
  }

  ngOnInit(): void {
    this.authService.getProviders().subscribe({
      next: (providers) => this.runInAngular(() => {
        this.googleAvailable = providers.google;
        this.isProviderLoading = false;
      }),
      error: () => this.runInAngular(() => {
        this.googleAvailable = false;
        this.isProviderLoading = false;
      })
    });
  }

  continueWithGoogle(): void {
    if (this.isLoading || this.isProviderLoading) {
      return;
    }
    if (!this.googleAvailable) {
      this.errorMessage = 'GOOGLE_SIGN_UP_SETUP_PENDING';
      return;
    }
    this.authService.startGoogleSignIn('/');
  }

  onPasswordChange(): void {
    this.passwordRequirements = this.authService.validatePassword(this.password);
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onSubmit(): void {
    if (!this.isFormValid()) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    this.authService.register({
      email: this.email,
      password: this.password,
      name: this.name || undefined
    }).subscribe({
      next: (response) => {
        this.runInAngular(() => {
          this.isLoading = false;
          if (response.success) {
            this.router.navigate(['/']);
          }
        });
      },
      error: (error) => {
        this.runInAngular(() => {
          this.isLoading = false;
          this.errorMessage = this.resolveErrorKey(error, 'REGISTER_ERROR');
        });
      }
    });
  }

  isFormValid(): boolean {
    return (
      this.authService.isEmailValid(this.email) &&
      this.authService.isPasswordValid(this.password) &&
      this.password === this.confirmPassword &&
      this.acceptTerms
    );
  }

  isEmailValid(): boolean {
    return this.email.length === 0 || this.authService.isEmailValid(this.email);
  }

  passwordsMatch(): boolean {
    return this.confirmPassword.length === 0 || this.password === this.confirmPassword;
  }

  get allRequirementsMet(): boolean {
    return Object.values(this.passwordRequirements).every(Boolean);
  }

  private resolveErrorKey(error: any, fallbackKey: string): string {
    const message = error?.error?.message || error?.message;
    if (typeof message === 'string' && /^[A-Z0-9_]+$/.test(message)) {
      return message;
    }
    return fallbackKey;
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }
}
